import React, { FC, useState, useEffect, useRef } from "react";
import { Table, TableProps, Tooltip } from "antd";
import type { FilterValue, Key } from "antd/lib/table/interface";
import {
  FilterFilled,
  CheckCircleFilled,
  CloseCircleFilled,
  PushpinOutlined,
  PushpinFilled,
  HeartOutlined,
  HeartFilled,
} from "@ant-design/icons";
import {
  AccruedFee,
  formatPrice,
  MatterFilter,
  MatterStatus,
  RelevantMatter,
  RelevantMatterWithAccruedFees,
} from "../../data-types";
import { GpmOrSlider } from "../gpm-slider";
import { GpmType } from "../../data-types";
import { useGpmSettingsCheck } from "~/hooks";
import { SorterResult } from "antd/es/table/interface";
import {
  createClientFilterWithPreselection,
  filterOrgUnit,
  filterUpdatedAt,
  filterTitle,
} from "~/containers/matter-table/filter";
import { useOrganisationalUnitsQuery } from "~/graphql/query";
import { StatusFilter } from "../filter/status-filter/status-filter";
import { CheckboxFilter } from "../filter";
import { FilterDropdownProps } from "antd/lib/table/interface";

import styles from "./create-proposal.css";
import { useTaxonomyLabel, VPDLabel } from "~/context/taxonomy-context";

const {
  ["centered-column"]: centeredColumnClassName,
  ["lost-icon"]: lostIconClassName,
  ["won-icon"]: wonIconClassName,
  ["pinned-icon"]: pinnedIconClassName,
  ["unpinned-icon"]: unpinnedIconClassName,
  ["pinned-row"]: pinnedRowClassName,
  ["pin-button-wrapper"]: pinButtonWrapperClassName,
  ["favourited-icon"]: favouritedIconClassName,
  ["unfavourited-icon"]: unfavouritedIconClassName,
  ["favourite-button-wrapper"]: favouriteButtonWrapperClassName,
} = styles;

function getAvailableSpace(element: HTMLElement | null) {
  if (!element) return 0;
  const rect = element.getBoundingClientRect();
  const topOffset = rect.top;
  const viewportHeight = window.innerHeight;
  const availableSpace = viewportHeight - topOffset;
  return availableSpace;
}

const highlightText = (text: string, search?: string) => {
  if (!search) return text;

  const searchTerms = search
    .split(/[,.]/)
    .map((term) => term.trim())
    .filter(Boolean);

  const pattern = new RegExp(`(${searchTerms.join("|")})`, "gi");

  const parts = text.split(pattern);
  return parts.map((part, i) =>
    searchTerms.some((term) => part.toLowerCase() === term.toLowerCase()) ? (
      <span key={i} style={{ color: "red" }}>
        {part}
      </span>
    ) : (
      part
    )
  );
};

interface RelevantMatterListProps {
  matters: RelevantMatter[];
  accruedFees?: AccruedFee[];
  loading: boolean;
  selectedMatter: RelevantMatter | null;
  onMatterSelect: (matter: RelevantMatter | null) => void;
  renderRelevantMatterPage: () => React.ReactNode;
  matterFilter: MatterFilter;
  handleFilterChange: (filters: Record<string, FilterValue | null>) => void;
  additionalSearchText?: string;
  onPinToMatter?: (matterId: number) => void;
  pinnedMatterIds?: number[];
  onFavouriteMatter?: (matterId: number) => void;
}

export const RelevantMatterList: FC<RelevantMatterListProps> = ({
  matters,
  accruedFees,
  loading,
  selectedMatter,
  onMatterSelect,
  renderRelevantMatterPage,
  matterFilter,
  handleFilterChange,
  additionalSearchText,
  onPinToMatter,
  pinnedMatterIds = [],
  onFavouriteMatter,
}) => {
  const { showGpm, showGpmSlider } = useGpmSettingsCheck();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortedInfo, setSortedInfo] = useState<SorterResult<RelevantMatterWithAccruedFees>>({});
  const [tableHeight, setTableHeight] = useState(300);
  const [pageSize, setPageSize] = useState(6);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [statusFilterUI, setStatusFilterUI] = useState<string[]>([]);

  const taxonomy = useTaxonomyLabel();

  useEffect(() => {
    const availableSpace = getAvailableSpace(tableContainerRef.current);
    setTableHeight(availableSpace);
    if (availableSpace) {
      setPageSize(Math.floor(availableSpace / 90));
    }
  }, []);

  const mattersWithAccruedFees: RelevantMatterWithAccruedFees[] = matters.map((matter) => ({
    ...matter,
    key: matter.id,
    accruedFees: accruedFees?.find((fee) => fee.matterId === matter.id),
  }));

  const paginationConfig = {
    onChange: (page: number) => setCurrentPage(page),
    total: matters.length,
    pageSize,
    current: currentPage,
    showSizeChanger: false,
  };

  const handleTableChange: TableProps<RelevantMatterWithAccruedFees>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    setStatusFilterUI((filters.status as string[]) || []);

    const statusFilter =
      !filters.status || filters.status.length === 0
        ? Object.values(MatterStatus)
        : [
            ...(filters.status?.includes("Won") ? [MatterStatus.Closed, MatterStatus.Live] : []),
            ...(filters.status?.includes(MatterStatus.Lost) ? [MatterStatus.Lost] : []),
          ];

    handleFilterChange({
      ...filters,
      status: statusFilter,
    });
    setSortedInfo(sorter as SorterResult<RelevantMatterWithAccruedFees>);
  };

  const { data: orgUnits } = useOrganisationalUnitsQuery();

  return selectedMatter ? (
    <>{renderRelevantMatterPage()}</>
  ) : (
    <div>
      {/* <div className={searchPaginationContainerClassName}>
        <div className={paginationWrapperClassName}>
          <Pagination {...paginationConfig} />
        </div>
      </div> */}
      <div ref={tableContainerRef}>
        <Table
          loading={loading}
          dataSource={mattersWithAccruedFees}
          onChange={handleTableChange}
          scroll={{ y: tableHeight - 150 }}
          columns={[
            {
              title: sortedInfo.column ? "Title" : "Title (showing most relevant first)",
              dataIndex: "title",
              key: "title",
              width: 250,
              render: (text: string, matter: RelevantMatterWithAccruedFees) => (
                <span>{highlightText(matter.title, additionalSearchText)}</span>
              ),
              sorter: (a: RelevantMatterWithAccruedFees, b: RelevantMatterWithAccruedFees) =>
                a.title.localeCompare(b.title),
              filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
              filterDropdown: (values) =>
                filterTitle({
                  ...values,
                  defaultValue: matterFilter.title || undefined,
                  setSelectedKeys: (key: string) => values.setSelectedKeys([key]),
                }),
              filteredValue: matterFilter.title ? [matterFilter.title] : undefined,
            },
            ...(accruedFees
              ? [
                  {
                    title: "Client",
                    dataIndex: ["client", "name"],
                    key: "client",
                    render: (text: string, matter: RelevantMatterWithAccruedFees) =>
                      highlightText(matter.client?.name || "", additionalSearchText),
                    width: 150,
                    filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
                    filterDropdown: createClientFilterWithPreselection(matterFilter.client || []),
                    filteredValue: matterFilter.client || null,
                  },
                ]
              : []),
            {
              title: `${taxonomy.PracticeArea}`,
              dataIndex: ["organisationalUnit", "name"],
              key: "orgUnit",
              width: 100,
              filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
              filterDropdown: filterOrgUnit(orgUnits?.organisationalUnits || []),
              filteredValue: matterFilter.orgUnit ? [matterFilter.orgUnit] : null,
            },
            ...(accruedFees
              ? [
                  {
                    title: "Updated At",
                    dataIndex: ["updatedAt"],
                    key: "updatedAt",
                    width: 100,
                    filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
                    filterDropdown: filterUpdatedAt,
                    filteredValue: matterFilter.updatedAt
                      ? [
                          matterFilter.updatedAt.from?.getTime() || 0,
                          matterFilter.updatedAt.to?.getTime() || 0,
                        ]
                      : null,
                    sorter: (a: RelevantMatterWithAccruedFees, b: RelevantMatterWithAccruedFees) =>
                      new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
                    render: (value: string) => new Date(value).toLocaleDateString(),
                  },
                  {
                    title: "GPM",
                    dataIndex: ["accruedFees"],
                    key: "actualGpm",
                    width: !showGpm && showGpmSlider ? 130 : 80,
                    sorter: (a: RelevantMatterWithAccruedFees, b: RelevantMatterWithAccruedFees) =>
                      (a.accruedFees?.actualGpm || 0) - (b.accruedFees?.actualGpm || 0),
                    filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
                    filterDropdown: ({
                      setSelectedKeys,
                      confirm,
                      clearFilters,
                    }: FilterDropdownProps) => (
                      <CheckboxFilter
                        options={[
                          { label: "Only show matters with time entries", value: "hasTimeEntries" },
                        ]}
                        defaultValue={matterFilter.someTimeEntries ? ["hasTimeEntries"] : []}
                        onApplyFilter={(value) => {
                          setSelectedKeys(value as Key[]);
                          confirm();
                        }}
                        onClearFilter={() => {
                          setSelectedKeys([]);
                          clearFilters?.();
                          confirm();
                        }}
                      />
                    ),
                    filteredValue: matterFilter.someTimeEntries ? ["hasTimeEntries"] : [],
                    render: (record: AccruedFee) => {
                      const sliderWidth = !showGpm && showGpmSlider ? 130 : 80;
                      return record.mostRecentTimeEntry ? (
                        <GpmOrSlider
                          showGpm={showGpm}
                          showGpmSlider={showGpmSlider}
                          gpm={{ type: GpmType.Fixed, fixed: record.actualGpm }}
                          gpmThreshold={{
                            gpmDangerThreshold: record.gpmDangerThreshold,
                            gpmWarningThreshold: record.gpmWarningThreshold,
                            source: "matter",
                          }}
                          isFeeEarnerLevelGpm={false}
                          showRequired={false}
                          gpmSliderStyle={{ maxWidth: sliderWidth, width: sliderWidth }}
                        />
                      ) : (
                        "N/A"
                      );
                    },
                  },
                  {
                    title: "Fee",
                    dataIndex: ["accruedFees"],
                    key: "actualFee",
                    width: 80,
                    sorter: (a: RelevantMatterWithAccruedFees, b: RelevantMatterWithAccruedFees) =>
                      (a.accruedFees?.actualFee || 0) - (b.accruedFees?.actualFee || 0),
                    render: (record: AccruedFee) => (
                      <div>{formatPrice(record.actualFee, record.currencySymbol)}</div>
                    ),
                  },
                  {
                    title: "Won/Lost",
                    dataIndex: ["status"],
                    key: "status",
                    width: 80,
                    className: centeredColumnClassName,
                    filters: [
                      { text: "Won", value: "Won" },
                      { text: "Lost", value: MatterStatus.Lost },
                    ],
                    filterDropdown: ({
                      setSelectedKeys,
                      selectedKeys,
                      confirm,
                      clearFilters,
                    }: FilterDropdownProps) => (
                      <StatusFilter
                        defaultValue={selectedKeys as string[]}
                        onApplyFilter={(value) => {
                          setSelectedKeys(value);
                          confirm();
                        }}
                        onClearFilter={() => {
                          setSelectedKeys([]);
                          clearFilters?.();
                          confirm();
                        }}
                      />
                    ),
                    filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
                    filteredValue: statusFilterUI,
                    render: (record: MatterStatus) =>
                      record === MatterStatus.Lost ? (
                        <CloseCircleFilled className={lostIconClassName} />
                      ) : record === MatterStatus.Closed || record === MatterStatus.Live ? (
                        <CheckCircleFilled className={wonIconClassName} />
                      ) : null,
                  },
                ]
              : []),
            {
              title: "Relevance",
              dataIndex: ["relevanceScore"],
              key: "relevanceScore",
              width: 80,
              sorter: (a: RelevantMatterWithAccruedFees, b: RelevantMatterWithAccruedFees) =>
                (a.relevanceScore || 0) - (b.relevanceScore || 0),
              render: (value: number) => <div>{(100 * value).toFixed(0)}%</div>,
            },
            ...(onPinToMatter
              ? [
                  {
                    title: (
                      <Tooltip title="Pin matters to find them in the workspace">
                        <PushpinOutlined style={{ fontSize: "16px" }} />
                      </Tooltip>
                    ),
                    key: "pin",
                    width: 40,
                    align: "center" as const,
                    render: (_: unknown, record: RelevantMatterWithAccruedFees) => {
                      const isPinned = pinnedMatterIds.includes(record.id);

                      return isPinned ? (
                        <div
                          className={pinButtonWrapperClassName}
                          onClick={(e) => {
                            e.stopPropagation();
                            onPinToMatter(record.id);
                          }}
                        >
                          <PushpinFilled
                            className={pinnedIconClassName}
                            title="Click to unpin this matter"
                          />
                        </div>
                      ) : (
                        <div
                          className={pinButtonWrapperClassName}
                          onClick={(e) => {
                            e.stopPropagation();
                            onPinToMatter(record.id);
                          }}
                        >
                          <PushpinOutlined
                            className={unpinnedIconClassName}
                            title="Click to pin this matter"
                          />
                        </div>
                      );
                    },
                  },
                ]
              : []),
            ...(onFavouriteMatter
              ? [
                  {
                    title: (
                      <Tooltip title="Favourite matters for quick access">
                        <HeartOutlined style={{ fontSize: "16px" }} />
                      </Tooltip>
                    ),
                    key: "favourite",
                    width: 40,
                    align: "center" as const,
                    render: (_: unknown, record: RelevantMatterWithAccruedFees) => {
                      const isFavourited = record.isFavourite;

                      return isFavourited ? (
                        <div
                          className={favouriteButtonWrapperClassName}
                          onClick={(e) => {
                            e.stopPropagation();
                            onFavouriteMatter?.(record.id);
                          }}
                        >
                          <HeartFilled
                            className={favouritedIconClassName}
                            title="Click to unfavourite this matter"
                          />
                        </div>
                      ) : (
                        <div
                          className={favouriteButtonWrapperClassName}
                          onClick={(e) => {
                            e.stopPropagation();
                            onFavouriteMatter?.(record.id);
                          }}
                        >
                          <HeartOutlined
                            className={unfavouritedIconClassName}
                            title="Click to favourite this matter"
                          />
                        </div>
                      );
                    },
                  },
                ]
              : []),
          ]}
          onRow={(record) => ({
            onClick: () => onMatterSelect(record),
            className: pinnedMatterIds.includes(record.id) ? pinnedRowClassName : "",
          })}
          // pagination={{
          //   ...paginationConfig,
          //   position: [],
          // }}
          pagination={false}
        />
      </div>
    </div>
  );
};
